/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import React, { lazy } from "react";
import { Logger, redBright } from "../utils/logger";
import { ComponentDebug } from "./_component-debug";
import { isNull } from "lodash";
import { Loadable } from "./Loadable";
const CustomerComponents = Loadable(lazy(() => import("../../customer-components")));

import { AccordionComponent } from "./accordion";
import { ButtonCtaComponent } from "./button-cta";
import { CallToActionComponent } from "./call-to-action";
import { CardsComponent } from "./cards";
import { CardsSimpleComponent } from "./cards-simple";
import { CivilSpaceComponent } from "./civilspace";
import { CityVizComponent } from "./cityviz";
import { ContactCardComponent } from "./contact-card";
import { DataTableComponent } from "./data-table";
import { DividerComponent } from "./divider";
import { DocumentListComponent } from "./document-list";
import { EmbedComponent } from "./embed";
import { WebComponentEmbedComponent } from "./embed-webcomponent";
import { FeatureAccordionComponent } from "./feature-accordion";
import { FeatureWebpageComponent } from "./feature-webpage";
import { FeedbackComponent } from "./feedback";
import { FeedComponent } from "./feed";
import { FeedComponentCalendar } from "./calendar";
import { FormsComponent } from "./forms";
import { HeadingComponent } from "./heading";
import { Hero } from "./hero";
import { HeroCarousel } from "./hero-carousel";
import { HeroSlideshow } from "./hero-slideshow";
import { HighlightComponent } from "./highlight";
import { IconCtaComponent } from "./icon-cta";
import { IconLinkTabsComponent } from "./icon-link-tabs";
import { IconLinkListComponent } from "./icon-link-list";
import { ImageComponent } from "./image";
import { ImageGalleryComponent } from "./image-gallery";
import { ImageLinkComponent } from "./image-link";
import { ImageListComponent } from "./image-list";
import { InfoBoxComponent } from "./info-box";
import { LinkButtonComponent } from "./link-button";
import { LinkListComponent } from "./link-list";
import { NavCardsComponent } from "./nav-cards";
import { NavCardsSimpleComponent } from "./nav-cards-simple";
import { NavigationListComponent } from "./navigation-list";
import { NewsAndEventsFeedComponent } from "./news-events-feed";
import { NewsletterCtaComponent } from "./newsletter-cta";
import { PermitDisplayComponent } from "./permit-display";
import { PermitSelectComponent } from "./permit-select";
import { ProcessCardsComponent } from "./process-cards";
import { MyPermitDisplayComponent } from "./my-permit-display";
import { QuoteComponent } from "./quote";
import { RichTextComponent } from "./rich-text/rich-text";
import { RichTextWithImageComponent } from "./rich-text-with-image";
import { RoutewareCalendarComponent } from "./routeware-calendar";
import { RoutewareWizardComponent } from "./routeware-wizard";
import { ScrollRevealImage } from "./scroll-reveal";
import { SectionLinkCardsComponent } from "./section-link-cards";
import { TabGroupComponent } from "./tab-group";
import { TestimonialHighlight } from "./testimonial-highlight";
import { Testimonial } from "./testimonial";
import { TimelineComponent } from "./timeline";
import { TocComponent } from "./toc";
import { YoutubeComponent } from "./youtube";

// Customer Components should be loaded from src/customer-components.jsx

export const getComponent = (component, field, webpage) => {
    // Logger.info(greenBright(`getComponent [type:${component?.type}]`));
    const visual_debug = false;
    if (visual_debug) {
        return (
            <ComponentDebug
                component={component}
                field={field}
            />
        );
    }

    const sc_component = getSimpliCityComponent(component, field, webpage);

    // Logger.info(greenBright(`Loaded Component instance for [displayName:${sc_component?.displayName}] [type:${component?.type}]`));
    // Logger.info(greenBright(`Loaded Component instance for [type:${component?.type}]`));

    if (isNull(sc_component)) {
        // Jump out to include customer components with dynamic import
        return (
            <CustomerComponents
                component={component}
                field={field}
                webpage={webpage}
            />
        );

    } else {
        const isValid = React.isValidElement(sc_component);
        // const isNotFoundInstance = sc_component.type.displayName === "ComponentNotFound";
        // const instance = sc_component.type.displayName;
        // Logger.info(greenBright(`Loaded [valid:${isValid}] Component instance for [instance:${instance}] [type:${component?.type}]`));
        // Logger.info(greenBright(`Loaded [valid:${isValid}] Component instance for [type:${component?.type}]`));
        if (!isValid) {
            Logger.info(redBright(`Loaded [valid:${isValid}] Component instance for [type:${component?.type}]`));
        }
        return sc_component;
    }
};

export const getSimpliCityComponent = (component, field, webpage) => {
    const type = component?.type;
    switch (type) {
        case "component.accordion":
            return (
                <AccordionComponent
                    component={component}
                    field={field}
                />
            );

        case "component.button-cta":
            return (
                <ButtonCtaComponent
                    component={component}
                    field={field}
                />
            );

        case "component.call-to-action":
            return (
                <CallToActionComponent
                    component={component}
                    field={field}
                />
            );

        case "component.cards":
            return (
                <CardsComponent
                    component={component}
                    field={field}
                />
            );

        case "component.cards-simple":
            return (
                <CardsSimpleComponent
                    component={component}
                    field={field}
                />
            );

        case "component.civilspace-engagement":
            return (
                <CivilSpaceComponent
                    component={component}
                    field={field}
                />
            );

        case "component.cityviz":
            return (
                <CityVizComponent
                    component={component}
                    field={field}
                />
            );

        case "component.contact-card":
            return (
                <ContactCardComponent
                    component={component}
                    field={field}
                />
            );

        case "component.data-table":
            return (
                <DataTableComponent
                    component={component}
                    field={field}
                />
            );

        case "component.divider":
            return (
                <DividerComponent
                    component={component}
                    field={field}
                />
            );

        case "component.document-list":
            return (
                <DocumentListComponent
                    component={component}
                    field={field}
                />
            );

        case "component.embed":
            return (
                <EmbedComponent
                    component={component}
                    field={field}
                />
            );

        case "component.embed-web-component":
            return (
                <WebComponentEmbedComponent
                    component={component}
                    field={field}
                />
            );

        case "component.feature-accordion":
            return (
                <FeatureAccordionComponent
                    component={component}
                    field={field}
                />
            );

        case "component.feature-webpage":
            return (
                <FeatureWebpageComponent
                    component={component}
                    field={field}
                />
            );

        case "component.feedback":
            return (
                <FeedbackComponent
                    component={component}
                    field={field}
                />
            );

        case "component.feed-model":
            return (
                <FeedComponent
                    component={component}
                    field={field}
                />
            );

        case "component.feed-calendar":
            return (
                <FeedComponentCalendar
                    component={component}
                    field={field}
                />
            );

        case "component.form":
            return (
                <FormsComponent
                    component={component}
                    field={field}
                />
            );

        case "component.heading":
            return (
                <HeadingComponent
                    component={component}
                    field={field}
                />
            );

        case "component.hero":
            return (
                <Hero
                    component={component}
                    field={field}
                />
            );

        case "component.hero-carousel":
            return (
                <HeroCarousel
                    component={component}
                    field={field}
                />
            );

        case "component.hero-slideshow":
            return (
                <HeroSlideshow
                    component={component}
                    field={field}
                />
            );

        case "component.highlight":
            return (
                <HighlightComponent
                    component={component}
                    field={field}
                />
            );

        case "component.icon-cta":
            return (
                <IconCtaComponent
                    component={component}
                    field={field}
                />
            );

        case "component.icon-link-tabs":
            return (
                <IconLinkTabsComponent
                    component={component}
                    field={field}
                />
            );

        case "component.icon-links-list":
            return (
                <IconLinkListComponent
                    component={component}
                    field={field}
                />
            );

        case "component.image":
            return (
                <ImageComponent
                    component={component}
                    field={field}
                />
            );

        case "component.image-gallery":
            return (
                <ImageGalleryComponent
                    component={component}
                    field={field}
                />
            );

        case "component.image-link":
            return (
                <ImageLinkComponent
                    component={component}
                    field={field}
                />
            );

        case "component.image-list":
            return (
                <ImageListComponent
                    component={component}
                    field={field}
                />
            );

        case "component.info-box":
            return (
                <InfoBoxComponent
                    component={component}
                    field={field}
                />
            );

        case "component.link-button":
            return (
                <LinkButtonComponent
                    component={component}
                    field={field}
                />
            );

        case "component.links-list":
            return (
                <LinkListComponent
                    component={component}
                    field={field}
                />
            );

        case "component.nav-cards":
            return (
                <NavCardsComponent
                    component={component}
                    field={field}
                />
            );

        case "component.nav-cards-simple":
            return (
                <NavCardsSimpleComponent
                    component={component}
                    field={field}
                />
            );

        case "component.navigation-list":
            return (
                <NavigationListComponent
                    component={component}
                    webpage={webpage}
                    field={field}
                />
            );

        case "component.news-events-feed":
            return (
                <NewsAndEventsFeedComponent
                    component={component}
                    field={field}
                />
            );

        case "component.newsletter-cta":
            return (
                <NewsletterCtaComponent
                    component={component}
                    field={field}
                />
            );

        case "component.permit-list":
            return (
                <PermitDisplayComponent
                    component={component}
                    field={field}
                />
            );

        case "component.permit-select":
            return (
                <PermitSelectComponent
                    component={component}
                    field={field}
                />
            );

        case "component.process-cards":
            return (
                <ProcessCardsComponent
                    component={component}
                    field={field}
                />
            );

        case "component.permit-list-auth":
            return (
                <MyPermitDisplayComponent
                    component={component}
                    field={field}
                />
            );

        case "component.quote":
            return (
                <QuoteComponent
                    component={component}
                    field={field}
                />
            );

        // rich-text-editor is the legacy implementation
        case "component.rich-text-editor":
        case "component.rich-text":
            return (
                <RichTextComponent
                    component={component}
                    field={field}
                />
            );

        case "component.rich-text-with-image":
            return (
                <RichTextWithImageComponent
                    component={component}
                    field={field}
                />
            );

        case "component.routeware-calendar":
            return (
                <RoutewareCalendarComponent
                    component={component}
                    field={field}
                />
            );

        case "component.routeware-wizard":
            return (
                <RoutewareWizardComponent
                    component={component}
                    field={field}
                />
            );

        case "component.scroll-reveal-image":
            return (
                <ScrollRevealImage
                    component={component}
                    field={field}
                />
            );

        case "component.section-link-cards":
            return (
                <SectionLinkCardsComponent
                    component={component}
                    field={field}
                />
            );

        case "component.tab-panel":
            return (
                <TabGroupComponent
                    component={component}
                    field={field}
                />
            );

        case "component.table-of-contents":
            return (
                <TocComponent
                    component={component}
                    webpage={webpage}
                    field={field}
                />
            );

        case "component.testimonial-highlight":
            return (
                <TestimonialHighlight
                    component={component}
                    field={field}
                />
            );

        case "component.testimonials":
            return (
                <Testimonial
                    component={component}
                    field={field}
                />
            );

        case "component.timeline":
            return (
                <TimelineComponent
                    component={component}
                    field={field}
                />
            );

        case "component.youtube":
            return (
                <YoutubeComponent
                    component={component}
                    field={field}
                />
            );

        default:
            return null;
    }
};
